import type { JSX } from 'preact';
import { currencySymbol } from '@/lib/format';
import { Input, type InputProps } from './Input';

export interface NumberInputProps extends Omit<InputProps, 'type' | 'value' | 'onInput'> {
  value: number | null | undefined;
  onValueChange: (value: number | null) => void;
  min?: number;
  max?: number;
  step?: number | 'any';
  precision?: number;
  currency?: string;
  percentage?: boolean;
}

function clamp(n: number, min?: number, max?: number): number {
  if (min !== undefined && n < min) return min;
  if (max !== undefined && n > max) return max;
  return n;
}

export function NumberInput({
  value,
  onValueChange,
  min,
  max,
  step = 'any',
  precision,
  currency,
  percentage,
  leftAddon,
  rightAddon,
  ...rest
}: NumberInputProps) {
  const handleInput = (event: JSX.TargetedEvent<HTMLInputElement>) => {
    const raw = event.currentTarget.value;
    if (raw === '') {
      onValueChange(null);
      return;
    }
    const parsed = Number(raw);
    if (Number.isNaN(parsed)) return;
    const clamped = clamp(parsed, min, max);
    const rounded = precision !== undefined ? Number(clamped.toFixed(precision)) : clamped;
    onValueChange(rounded);
  };

  return (
    <Input
      type="number"
      inputMode="decimal"
      value={value === null || value === undefined ? '' : String(value)}
      min={min}
      max={max}
      step={step}
      leftAddon={leftAddon ?? (currency ? currencySymbol(currency) : undefined)}
      rightAddon={rightAddon ?? (percentage ? '%' : undefined)}
      onInput={handleInput}
      {...rest}
    />
  );
}
